import React, { useState, useEffect } from 'react';
import { BASE_URL } from '../../config';
import useAuth from '../../hooks/useAuth';
import { showConfirm, showSuccess, showError } from '../../utils/dialog';
import { formatAppDateTime } from '../../utils/appDateTime';
import usePageMeta from '../../hooks/usePageMeta';
import CustomerLayout from './Layout';
import ActionDropdown from '../../components/ActionDropdown';
import { DollarSign, MoreHorizontal, FileCheck, Download } from "lucide-react";
import BackButton from "../../components/BackButton";
import DateInput from "../../components/DateInput";
import AdminFilterPanel from '../../components/AdminFilterPanel';
import { FaSlidersH } from 'react-icons/fa';

const Earnings = () => {

  usePageMeta("Earnings");
  const user = useAuth();

  const [summary, setSummary] = useState({
    total_earned: 0,
    pending_amount: 0,
    completed_tasks: 0
  });
  const [lifetimeSummary, setLifetimeSummary] = useState({
    total_earned: 0,
    pending_amount: 0,
    completed_tasks: 0
  });
  const [history, setHistory] = useState([]);
  const [selectedTab, setSelectedTab] = useState("Payment History");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [openFilterPanel, setOpenFilterPanel] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(15);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
        const res = await fetch(`${BASE_URL}/api/tempstaff/earnings/summary`, {
          credentials: 'include',
          headers,
        });
        const data = await res.json();
        // endpoint now returns { data: { monthly: {...}, all_time: {...} } }
        if (data.success && data.data) {
          if (data.data.monthly) setSummary(data.data.monthly);
          if (data.data.all_time) setLifetimeSummary(data.data.all_time);
        }
      } catch (err) {
        console.error("Failed to fetch earnings summary", err);
      }
    };
    const fetchHistory = async () => {
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
        const res = await fetch(`${BASE_URL}/api/tempstaff/earnings/history`, {
          credentials: 'include',
          headers,
        });
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) setHistory(data.data);
      } catch (err) {
        console.error("Failed to fetch earnings history", err);
      }
    };
    // Only fetch when we have a token (user is logged in)
    if (!user?.token) {
      console.log('Earnings: no auth token yet, skipping fetch');
      setLoading(false);
      return;
    }

    setLoading(true);
    Promise.all([fetchSummary(), fetchHistory()]).finally(() => setLoading(false));
  }, [user?.token]);

  // Allow callers to trigger a refresh after actions like delete
  useEffect(() => {
    if (!user?.token) return;
    const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
    (async () => {
      setLoading(true);
      try {
        const [resSum, resHist] = await Promise.all([
          fetch(`${BASE_URL}/api/tempstaff/earnings/summary`, { credentials: 'include', headers }),
          fetch(`${BASE_URL}/api/tempstaff/earnings/history`, { credentials: 'include', headers })
        ]);
        const [dataSum, dataHist] = await Promise.all([resSum.json().catch(()=>({})), resHist.json().catch(()=>({}))]);
        if (dataSum.success && dataSum.data) {
          if (dataSum.data.monthly) setSummary(dataSum.data.monthly);
          if (dataSum.data.all_time) setLifetimeSummary(dataSum.data.all_time);
        }
        if (dataHist.success && Array.isArray(dataHist.data)) setHistory(dataHist.data);
      } catch (err) {
        console.error('Earnings refresh failed', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.token, reloadCounter]);

  const handleDownloadInvoice = (shiftId) => {
    (async () => {
      try {
        const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
        const resp = await fetch(`${BASE_URL}/api/tempstaff/earnings/invoice/${shiftId}`, {
          method: 'GET',
          credentials: 'include',
          headers,
        });
        if (!resp.ok) {
          const json = await resp.json().catch(()=>({}));
          const msg = (json && json.message) ? json.message : 'Failed to download invoice';
          showError(msg);
          return;
        }
        const blob = await resp.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = `Invoice_shift_${shiftId}.pdf`;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error('Invoice download error', err);
        showError('Network error while downloading invoice');
      }
    })();
  };

  const handleDeleteShift = async (shiftId) => {
    const ok = await showConfirm('Are you sure you want to delete this earning? This action cannot be undone.');
    if (!ok) return;
    try {
      const headers = user?.token ? { Authorization: `Bearer ${user.token}` } : {};
      const resp = await fetch(`${BASE_URL}/api/jobs/shifts/${shiftId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      const data = await resp.json().catch(()=>({}));
      if (!resp.ok) {
        const msg = data.message || 'Failed to delete earning';
        showError(msg);
        return;
      }
      showSuccess(data.message || 'Earning deleted');
      // trigger refresh
      setReloadCounter(c => c + 1);
    } catch (err) {
      console.error('Delete shift error', err);
      showError('Network error while deleting earning');
    }
  };

  // Helper to get current filtered rows
  const getFilteredHistory = () => {
    // Group and deduplicate as before
    const groups = new Map();
    history.forEach(r => {
      const key = r.shift_id;
      if (!groups.has(key)) groups.set(key, { row: r, cnt: 1 });
      else groups.get(key).cnt += 1;
    });
    const displayed = Array.from(groups.values()).map(g => ({ ...g.row, dupCount: g.cnt }));
    // Filtering logic (already used in map)
    return displayed.filter(row => {
      if (searchTerm && !String(row.job).toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filterBy !== 'all' && row.status !== filterBy) return false;
      if (filterStatuses.length > 0 && !filterStatuses.includes(row.status)) return false;
      if (filterFromDate) {
        const d = new Date(row.start_date || row.submit_date || row.create_date);
        const from = new Date(filterFromDate);
        if (isNaN(d) || d < from) return false;
      }
      if (filterToDate) {
        const d = new Date(row.end_date || row.submit_date || row.create_date);
        const to = new Date(filterToDate);
        if (isNaN(d) || d > to) return false;
      }
      return true;
    });
  };

  const filteredHistory = getFilteredHistory();
  const totalItems = filteredHistory.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const currentPageSafe = Math.max(1, Math.min(currentPage, totalPages));
  const pageRows = filteredHistory.slice((currentPageSafe - 1) * itemsPerPage, currentPageSafe * itemsPerPage);

  // Pagination button logic (like Admin_Messages.js)
  const getVisiblePages = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    } else if (currentPageSafe <= 3) {
      return [1, 2, 3, '...', totalPages];
    } else if (currentPageSafe >= totalPages - 2) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    } else {
      return [1, '...', currentPageSafe - 1, currentPageSafe, currentPageSafe + 1, '...', totalPages];
    }
  };
  const visiblePages = getVisiblePages();

  return (
    <CustomerLayout active="earnings">
      <div className="py-2 px-2 flex flex-col min-h-screen">
        {/* Header */}
        <header className="justify-between ml-3 mb-2 ml-2">
          <div className="flex items-center gap-3">
          <BackButton />
          <h1 className="text-2xl font-semibold text-foreground">Earnings</h1>
          <button
            className="ml-4 p-2 hover:bg-gray-50 rounded transition-colors"
            onClick={() => setOpenFilterPanel(true)}
            title="Filter"
          >
            <FaSlidersH className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        </header>

        <div className="h-[1px] bg-[#a15d33] mx-3"></div>

        <div className="mt-3 mx-4 text-sm text-muted-foreground">
          Showing this month — change range
        </div>

  {/* status cards: compact 2x2 grid */}
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 mt-4 mx-4">
          {/* 1st card: Total Earned */}
          <div className="shadow border p-2 w-full rounded-md flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="text-textGray text-xs font-medium">
                Total Earned ( This Month )
              </h3>
              <div className="bg-amber-100 text-amber-500 rounded-full p-2">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">${summary.total_earned}</p>
            <hr className="border-gray-200 mb-1" />
            <div className="flex justify-between items-center text-xs text-textGray">
              <span>Awaiting payment release</span>
              <span className="text-gray-800 text-sm font-medium pr-2">{summary.pending_amount}</span>
            </div>
          </div>
          {/* 4th card: All-time Total Earned */}
          <div className="shadow border p-2 w-full rounded-md flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="text-textGray text-xs font-medium">
                Total Earned (All time)
              </h3>
              <div className="bg-amber-100 text-amber-500 rounded-full p-2">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">${lifetimeSummary.total_earned}</p>
            <hr className="border-gray-200 mb-1" />
            <div className="flex justify-between items-center text-xs text-textGray">
              <span>Awaiting payment release</span>
              <span className="text-gray-800 text-sm font-medium pr-2">{lifetimeSummary.pending_amount}</span>
            </div>
          </div>
          {/* 2nd card: Pending Payments */}
          <div className="shadow border p-2 w-full rounded-md flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="text-textGray text-xs font-medium">
                Pending Payments ( This Month )
              </h3>
              <div className="bg-red-500 text-white rounded-full p-2">
                <MoreHorizontal className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">${summary.pending_amount}</p>
            <hr className="border-gray-200 mb-1" />
            <div className="flex justify-between items-center text-xs text-textGray">
              <span>Completed Task</span>
              <span className="text-gray-800 text-sm font-medium pr-2">{summary.completed_tasks}</span>
            </div>
          </div>
          {/* 3rd card: Completed Tasks */}
          <div className="shadow border p-2 w-full rounded-md flex flex-col justify-between">
            <div className="flex justify-between items-center">
              <h3 className="text-textGray text-xs font-medium">
                Task Completed
              </h3>
              <div className="bg-[#00FF66] text-white rounded-full p-2">
                <FileCheck className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-semibold text-gray-900 mb-1">{summary.completed_tasks}</p>
            <hr className="border-gray-200 mb-1" />
            <div className="flex justify-between items-center text-xs text-textGray">
              <span>This Month</span>
              <span className="text-gray-800 text-sm font-medium pr-2"></span>
            </div>
          </div>
        </div>

        <div className="mt-6 mb-2 mx-3 p-3 border border-gray-200 rounded-xl h-full flex flex-col">
          <div className="flex space-x-4 mb-4 border-b border-border">
            {["Payment History"].map(tab => (
                <button
                  key={tab}
                  onClick={() => setSelectedTab(tab)}
                  className={`pb-2 px-1 text-sm text-[#DC1F00] font-medium border-b-2 transition-colors ${
                      selectedTab === tab
                      ? "border-[#DC1F00] text-sidebar-active"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                  >
                  {tab}
                </button>
              ))}
          </div>

          <div className="flex items-center justify-between mb-2">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 border border-gray-400 rounded-xl bg-card text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-sidebar-active/20 focus:border-sidebar-active w-56"
            />
          </div>

          <div className="mb-3 text-xs text-muted-foreground flex gap-6 items-center">
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">Sorted by:</span>
              <span className="text-foreground"></span>
            </div>
            <div className="flex items-center gap-2">
              <span className="whitespace-nowrap">Filter:</span>
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
                className="px-2 py-1 border border-gray-300 rounded-md text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-sidebar-active/20"
              >
                <option value="all">All</option>
              </select>
            </div>
          </div> 

          {selectedTab === "Payment History" && (
            loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : (
              <table className="min-w-full border shadow">
                <thead className="bg-[#dc1f0080] text-white">
                  <tr className="text-left select-none">
                    <th className="border text-sm font-medium pl-4 py-2 rounded-tl-lg w-1/5">Job</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">Start Date</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">End Date</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">Location</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">Pay Rate</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">Status</th>
                    <th className="border text-sm font-medium px-3 py-2 w-[15%]">Actions</th>
                    <th className="border text-sm font-medium px-3 py-2 rounded-tr-lg w-[3%]"></th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map((row, index) => (
                    <tr key={`${row.shift_id}-${index}`} className="text-left text-base">
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{row.job}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{formatAppDateTime(row.start_date)}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{formatAppDateTime(row.end_date)}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">{row.location}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">${row.pay_rate}</td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">
                        <span
                          className={`border border-border rounded px-2 py-0.5 capitalize 
                            ${row.status === 'paid' 
                              ? 'text-green-600 border-green-300 bg-green-50' 
                              : row.status === 'pending' 
                              ? 'text-amber-600 border-amber-300 bg-amber-50' 
                              : 'text-gray-500 border-gray-300 bg-gray-50'
                          }`}
                        >
                          {row.status}
                        </span>
                      </td>
                      <td className="pl-4 py-1.5 whitespace-normal break-words">
                        {row.status === "paid" && (
                          <button
                            onClick={() => handleDownloadInvoice(row.shift_id)}
                            className="flex items-center gap-2 bg-[#00000033] hover:bg-[#00000044] text-black text-sm px-3 py-1 rounded"
                          >
                            <Download size={16} />
                            Invoice
                          </button>
                        )}
                      </td>
                      <td className="py-1.5">
                        <div className="flex items-center gap-2">
                          <ActionDropdown
                            actions={[
                              { label: "Delete", onClick: () => handleDeleteShift(row.shift_id) }
                            ]} 
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}
          <AdminFilterPanel
            isOpen={openFilterPanel}
            title="Earnings Filters"
            onClose={() => setOpenFilterPanel(false)}
            onApply={() => setOpenFilterPanel(false)}
            onClear={() => { setFilterFromDate(''); setFilterToDate(''); setFilterStatuses([]); setFilterBy('all'); }}
          >
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-[#00000080] mb-1">From</label>
                <DateInput value={filterFromDate} onChange={setFilterFromDate} className="w-full border px-2 py-1 rounded" />
              </div>
              <div>
                <label className="block text-sm text-[#00000080] mb-1">To</label>
                <DateInput value={filterToDate} onChange={setFilterToDate} className="w-full border px-2 py-1 rounded" />
              </div>
              <div>
                <label className="block text-sm text-[#00000080] mb-1">Statuses</label>
                <div className="space-y-2">
                  {['paid','pending','other'].map(s=> (
                    <label key={s} className="flex items-center gap-2">
                      <input type="checkbox" checked={filterStatuses.includes(s)} onChange={(e)=>{
                        if (e.target.checked) setFilterStatuses(prev=>[...prev,s]); else setFilterStatuses(prev=>prev.filter(x=>x!==s));
                      }} />
                      <span className="capitalize">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </AdminFilterPanel>
        </div>

        {/* Pagination controller */}
        <div className="flex items-center justify-between mt-auto text-sm text-muted-foreground">
          <span>
            {totalItems === 0
              ? 'No jobs to show'
              : `Showing ${Math.min(itemsPerPage * (currentPageSafe - 1) + 1, totalItems)}–${Math.min(itemsPerPage * currentPageSafe, totalItems)} of ${totalItems} Jobs`}
          </span>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-2 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100"
              disabled={currentPageSafe === 1}
              onClick={() => setCurrentPage(currentPageSafe - 1)}
            >
              Previous
            </button>
            {visiblePages.map((page, idx) =>
              page === '...'
                ? (<span key={idx} className="px-3 py-2">...</span>)
                : (
                  <button
                    key={idx}
                    className={`px-3 py-2 border border-[#DC1F0033] rounded-md ${currentPageSafe === page ? "bg-[#DC1F00] text-white" : "hover:bg-gray-100"}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                )
            )}
            <button
              className="px-3 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md"
              disabled={currentPageSafe === totalPages}
              onClick={() => setCurrentPage(currentPageSafe + 1)}
            >
              Next
            </button>
          </div>
        </div>
        
      </div>
    </CustomerLayout>
  );
};

export default Earnings;
